import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import BattleRoom from '../components/battle-room-v2/components/BattleRoom';
import { useWebSocket } from '../contexts/WebSocketContext';
import './BattleRoomPage.css';

const BattleRoomPage = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const { joinSession, clearSessionState, currentSessionId, sessionStatus } = useWebSocket();
  const autoJoinAttemptRef = useRef(null);
  const battleRoomInstanceKey = useMemo(() => {
    // A same-route history replacement is used to clear one-shot dashboard
    // load data after the snapshot has been restored. React Router assigns a
    // new location key to that replacement, but it is still the same room.
    // Keying BattleRoom by location.key remounted the shell immediately after
    // a successful restore and reset its tokens/map back to an empty room.
    return `${location.pathname}:${sessionId || 'new'}`;
  }, [location?.pathname, sessionId]);

  // date_of_change: 2026-04-17 - When navigating to /battle with no sessionId (new room),
  // use useLayoutEffect (runs synchronously before paint) so localStorage and React state
  // are cleared BEFORE BattleRoom renders and reads effectiveSessionId. Using the async
  // leaveSession() here caused a race: the auto-rejoin effect in WebSocketContext would
  // fire first and re-join the old session before leaveSession's backend call finished.
  useLayoutEffect(() => {
    if (!sessionId && currentSessionId) {
      clearSessionState();
    }
  }, [sessionId, currentSessionId, clearSessionState]);

  // Auto-join session if sessionId is provided in URL
  // @date_of_change: 2025-09-26 - Production-safe auto-join with WebSocket ready wait
  useEffect(() => {
    if (!sessionId) {
      autoJoinAttemptRef.current = null;
      return;
    }

    const alreadyAttachedToSession = currentSessionId === sessionId
      && (sessionStatus === 'joining' || sessionStatus === 'joined' || sessionStatus === 'reconnecting');

    if (alreadyAttachedToSession) {
      // A completed attachment ends this attempt. If a later total reconnect
      // failure clears the context while the route stays mounted, the room is
      // allowed to join again instead of remaining Player-only forever.
      // date_of_change: 2026-08-18
      autoJoinAttemptRef.current = null;
      return;
    }

    if (autoJoinAttemptRef.current === sessionId) {
      return;
    }

    autoJoinAttemptRef.current = sessionId;

    if (sessionId) {
      console.log('[BattleRoomPage] Auto-joining session from URL:', sessionId);
      (async () => {
        try {
          // Record backend membership before WS join for Clerk-aware routes.
          const { apiFetch } = await import('../utils/apiFetch');
          await apiFetch(`/api/combat/${sessionId}/join`, { method: 'POST' });
        } catch (e) {
          console.warn('[BattleRoomPage] Backend join failed (continuing to WS):', e);
        }

        try {
          // JoinSession handles authenticated WebSocket readiness and role ACKs.
          await joinSession(sessionId);
        } catch (error) {
          console.error('[BattleRoomPage] Failed to auto-join WS session:', error);
          autoJoinAttemptRef.current = null;
        }
      })();
    }
  }, [sessionId, currentSessionId, joinSession, sessionStatus]);

  return (
    <div className="battleroom-page">
      <BattleRoom key={battleRoomInstanceKey} sessionId={sessionId} />
    </div>
  );
};

export default BattleRoomPage;
